import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logger } from '../logging/logger';

// React error boundary — catches render/lifecycle/constructor errors thrown by a
// subtree so one crashing region (a song card, the player, a modal, the routed
// page) degrades to a fallback instead of white-screening the whole app.
//
// NOTE(port): Angular only had a single global ErrorHandler — it could log a
// crash but not contain it to a subtree. React error boundaries let us isolate
// per-region, which the Angular app could not do. Crashes are still routed to
// the same diagnostic logger as window.onerror (see shared/logging), so a
// downloaded .log shows which boundary tripped and the component stack.
//
// Class component is required: error boundaries have no hooks equivalent
// (getDerivedStateFromError / componentDidCatch are class-only).

interface Props {
  /** Identifies the boundary in logs (e.g. "result-item", "player"). */
  name: string;
  /**
   * Rendered when the subtree has crashed. Either a static node, or a function
   * receiving retry() — which clears the error and re-mounts children. Defaults
   * to null (the region silently disappears).
   */
  fallback?: ReactNode | ((retry: () => void) => ReactNode);
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('app', 'react error boundary', {
      boundary: this.props.name,
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  private retry = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    const { fallback } = this.props;
    return typeof fallback === 'function' ? fallback(this.retry) : (fallback ?? null);
  }
}
